/**
 * Which page serves which standard.
 *
 * The sidebar can be flipped from its default grouping — by what somebody came to do — to a
 * grouping by standard, so that an audit conducted against one standard can be walked in the
 * order that standard is written, and so that somebody told to "do the 9001 part" can find out
 * what that means.
 *
 * Three things about this table are deliberate.
 *
 * It is editorial, and it lives in the frontend. Which clause a page serves is a fact about the
 * standards, not about ABAX's data; deriving it server-side from the registers would mean
 * inventing a signal that nothing measures. It is also one table rather than several, because a
 * second copy would drift from the first and nobody re-reads a table like this once it is written.
 *
 * Every entry carries a reference and a note. "Documents" under a folder labelled ISO 9001 tells
 * a reader almost nothing; "Documents — 7.5.2, the documents themselves and their control" tells
 * them why they are looking at it. The reference is most of the value here. References are our
 * own paraphrase: no standard text is reproduced, the same rule the management review input list
 * follows.
 *
 * And entries may have no page. A menu organised by standard implies coverage of that standard,
 * which is the failure this table has to be designed against — ISO 14001 has 34 tagged assets and
 * no environmental risks at all. So the things that do not exist are listed too, with the reason,
 * and render disabled. A branch is not complete merely because every route in it is reachable.
 */

export type StandardKey = 'iso27001' | 'iso9001' | 'iso14001' | 'gdpr'

export interface StandardEntry {
  /** The route. Absent when the entry is something ABAX does not hold yet. */
  to?: string
  label: string
  /** Two or three letters for the collapsed sidebar. Matches the task-grouped menu. */
  short: string
  /** Clause or control, our paraphrase. */
  ref: string
  /** One line on why this page answers that clause. */
  note: string
  adminOnly?: boolean
  /** Set instead of `to` when nothing exists. Rendered as the reason it is missing. */
  missing?: string
}

export interface StandardSection {
  header: string
  items: StandardEntry[]
}

export interface Standard {
  key: StandardKey
  label: string
  /** Shown above the tree when the coverage behind it is thin enough to mislead. */
  caveat?: string
  sections: StandardSection[]
}

export const standards: Standard[] = [
  {
    key: 'iso27001',
    label: 'ISO/IEC 27001',
    sections: [
      {
        header: 'Planning',
        items: [
          {
            to: '/risks',
            label: 'Risks',
            short: 'RI',
            ref: '6.1.2 – 6.1.3',
            note: 'The assessment and the treatment decisions, scored against the appetite.',
          },
          {
            to: '/soa',
            label: 'Statement of Applicability',
            short: 'SoA',
            ref: '6.1.3 d)',
            note: 'Every Annex A control, whether it applies, and why.',
          },
        ],
      },
      {
        header: 'Support',
        items: [
          {
            to: '/documents',
            label: 'Documents',
            short: 'DO',
            ref: '7.5',
            note: 'Mirrored from EQS: what is valid, who approved it, when it is next reviewed.',
          },
          {
            to: '/people',
            label: 'People & roles',
            short: 'PE',
            ref: 'A.5.2 – A.5.4',
            note: 'Who holds which security responsibility.',
          },
        ],
      },
      {
        header: 'The controls themselves',
        items: [
          {
            to: '/assets',
            label: 'Assets',
            short: 'AS',
            ref: 'A.5.9 – A.5.13',
            note: 'The inventory, its owners and its classification.',
          },
          {
            to: '/suppliers',
            label: 'Suppliers',
            short: 'SU',
            ref: 'A.5.19 – A.5.23',
            note: 'Which suppliers are in scope, what was agreed with them, what assurance exists.',
          },
          {
            to: '/ai-tools',
            label: 'AI tooling',
            short: 'AI',
            ref: 'A.5.23',
            note: 'Which AI services may be used, by whom, and until when each approval lapses.',
          },
          {
            to: '/access',
            label: 'Key cards',
            short: 'KC',
            ref: 'A.7.1 – A.7.4',
            note: 'Physical entry: who holds which credential and who lent it to them.',
          },
          {
            to: '/admin',
            label: 'Roles & access',
            short: 'AD',
            ref: 'A.5.15 – A.5.18',
            note: 'Who was granted what in this system, by whom, and when.',
            adminOnly: true,
          },
        ],
      },
      {
        header: 'Checking it works',
        items: [
          {
            to: '/readiness',
            label: 'Readiness',
            short: 'RD',
            ref: '9.1',
            note: 'What each register can and cannot currently evidence.',
          },
          {
            to: '/checks',
            label: 'Checks',
            short: 'CK',
            ref: '9.1',
            note: 'Recurring verification, performed and recorded against a schedule.',
          },
          {
            to: '/attest',
            label: 'Attestations',
            short: 'AT',
            ref: 'A.5.18',
            note: 'Periodic confirmation by owners, including access rights review.',
          },
          {
            to: '/meetings',
            label: 'Management review',
            short: 'MR',
            ref: '9.3',
            note: 'The required inputs, which were covered, and the decisions taken.',
          },
          {
            label: 'Internal audit',
            short: 'IA',
            ref: '9.2',
            note: 'The audit programme and its findings.',
            missing: 'no audit programme is held in this system',
          },
        ],
      },
      {
        header: 'Improvement',
        items: [
          {
            to: '/review',
            label: 'Discoveries',
            short: 'DI',
            ref: 'A.5.9',
            note: 'What the connectors found that no register accounts for.',
          },
          {
            label: 'Nonconformities',
            short: 'NC',
            ref: '10.2',
            note: 'Corrective action and its effectiveness.',
            missing: 'being migrated to EQS; no register here yet',
          },
        ],
      },
    ],
  },

  {
    key: 'iso9001',
    label: 'ISO 9001',
    sections: [
      {
        header: 'Planning',
        items: [
          {
            to: '/risks',
            label: 'Risks',
            short: 'RI',
            ref: '6.1',
            note: 'Risks and opportunities, and what was decided about them.',
          },
        ],
      },
      {
        header: 'Support',
        items: [
          {
            to: '/documents',
            label: 'Documents',
            short: 'DO',
            ref: '7.5.2 – 7.5.3',
            note: 'Documented information: approval, validity and review, mirrored from EQS.',
          },
          {
            to: '/assets',
            label: 'Assets',
            short: 'AS',
            ref: '7.1.3',
            note: 'Infrastructure the processes depend on.',
          },
          {
            to: '/people',
            label: 'People & roles',
            short: 'PE',
            ref: '5.3',
            note: 'Responsibilities and authorities. Competence records (7.2) are not held here.',
          },
          {
            to: '/registers',
            label: 'Reference data',
            short: 'RF',
            ref: '4.1 – 4.3',
            note: 'Legal entities, org units and locations — the boundary the system applies to.',
          },
        ],
      },
      {
        header: 'Operation',
        items: [
          {
            to: '/suppliers',
            label: 'Suppliers',
            short: 'SU',
            ref: '8.4',
            note: 'Externally provided processes, products and services, and their control.',
          },
        ],
      },
      {
        header: 'Checking it works',
        items: [
          {
            to: '/checks',
            label: 'Checks',
            short: 'CK',
            ref: '9.1',
            note: 'Monitoring and measurement, performed against a schedule.',
          },
          {
            to: '/attest',
            label: 'Attestations',
            short: 'AT',
            ref: '9.1',
            note: 'Owners confirming their records still hold.',
          },
          {
            to: '/meetings',
            label: 'Management review',
            short: 'MR',
            ref: '9.3',
            note: 'The required inputs, which were covered, and the decisions taken.',
          },
          {
            label: 'Internal audit',
            short: 'IA',
            ref: '9.2',
            note: 'The audit programme and its findings.',
            missing: 'no audit programme is held in this system',
          },
        ],
      },
      {
        header: 'Improvement',
        items: [
          {
            to: '/review',
            label: 'Discoveries',
            short: 'DI',
            ref: '10.1',
            note: 'What the connectors found that no register accounts for.',
          },
          {
            label: 'Nonconformities',
            short: 'NC',
            ref: '10.2',
            note: 'Corrective action and its effectiveness.',
            missing: 'being migrated to EQS; no register here yet',
          },
        ],
      },
    ],
  },

  {
    key: 'iso14001',
    label: 'ISO 14001',
    caveat:
      'Thin. 34 assets carry a 14001 tag and no risk does; there is no register of environmental ' +
      'aspects and no environmental objectives. The entries below are what exists, not a claim of ' +
      'coverage.',
    sections: [
      {
        header: 'Planning',
        items: [
          {
            label: 'Environmental aspects',
            short: 'EA',
            ref: '6.1.2',
            note: 'Which activities interact with the environment, and which of those matter.',
            missing: 'no register exists',
          },
          {
            label: 'Compliance obligations',
            short: 'CO',
            ref: '6.1.3',
            note: 'The environmental law and other obligations that bind ABAX.',
            missing: 'no register exists',
          },
          {
            to: '/risks',
            label: 'Risks',
            short: 'RI',
            ref: '6.1.1',
            note: 'Risks and opportunities. No risk currently carries a 14001 tag.',
          },
          {
            label: 'Environmental objectives',
            short: 'EO',
            ref: '6.2',
            note: 'Targets, and the plan to reach them.',
            missing: 'not started',
          },
        ],
      },
      {
        header: 'Support and operation',
        items: [
          {
            to: '/documents',
            label: 'Documents',
            short: 'DO',
            ref: '7.5',
            note: 'Documented information, mirrored from EQS.',
          },
          {
            to: '/assets',
            label: 'Assets',
            short: 'AS',
            ref: '8.1',
            note: 'The 34 assets tagged 14001, and their owners.',
          },
        ],
      },
      {
        header: 'Checking it works',
        items: [
          {
            to: '/checks',
            label: 'Checks',
            short: 'CK',
            ref: '9.1',
            note: 'Monitoring and measurement. None of the current checks is environmental.',
          },
          {
            to: '/meetings',
            label: 'Management review',
            short: 'MR',
            ref: '9.3',
            note: 'The required inputs, which were covered, and the decisions taken.',
          },
        ],
      },
    ],
  },

  {
    key: 'gdpr',
    label: 'GDPR',
    sections: [
      {
        header: 'What we process',
        items: [
          {
            to: '/ropa',
            label: 'Processing (GDPR)',
            short: 'PA',
            ref: 'Art. 30',
            note: 'The record of processing activities, including transfers out of the EEA.',
          },
          {
            to: '/assets',
            label: 'Assets',
            short: 'AS',
            ref: 'Art. 30(1)(g)',
            note: 'The systems the processing runs on, and their classification.',
          },
        ],
      },
      {
        header: 'Who else touches it',
        items: [
          {
            to: '/suppliers',
            label: 'Suppliers',
            short: 'SU',
            ref: 'Art. 28',
            note: 'Processors, what was agreed, and which of them still need an agreement.',
          },
          {
            to: '/documents',
            label: 'Documents',
            short: 'DO',
            ref: 'Art. 28(3)',
            note: 'The agreements themselves, indexed from House of Control.',
          },
        ],
      },
      {
        header: 'Keeping it secure',
        items: [
          {
            to: '/admin',
            label: 'Roles & access',
            short: 'AD',
            ref: 'Art. 32(1)(b)',
            note: 'Who can reach what, granted by whom.',
            adminOnly: true,
          },
          {
            to: '/access',
            label: 'Key cards',
            short: 'KC',
            ref: 'Art. 32(1)(b)',
            note: 'Physical access to the premises where processing happens.',
          },
          {
            to: '/checks',
            label: 'Checks',
            short: 'CK',
            ref: 'Art. 32(1)(d)',
            note: 'Regular testing of the measures, recorded when performed.',
          },
        ],
      },
      {
        header: 'Accountability',
        items: [
          {
            to: '/readiness',
            label: 'Readiness',
            short: 'RD',
            ref: 'Art. 5(2)',
            note: 'What can and cannot currently be demonstrated.',
          },
          {
            label: 'Data subject requests',
            short: 'DSR',
            ref: 'Art. 15 – 22',
            note: 'Requests received, and how they were answered.',
            missing: 'handled outside this system; no log here',
          },
          {
            label: 'Breach register',
            short: 'BR',
            ref: 'Art. 33 – 34',
            note: 'Personal data breaches and whether they were notified.',
            missing: 'no register exists',
          },
          {
            label: 'Impact assessments',
            short: 'DPIA',
            ref: 'Art. 35',
            note: 'DPIAs and their outcomes.',
            missing: 'the procedure is in EQS; completed assessments are not held here',
          },
        ],
      },
    ],
  },
]

/** What the sidebar opens on, and what it falls back to when a stored key no longer exists. */
export const defaultStandardKey: StandardKey = 'iso27001'

/**
 * Always returns a standard. A key stored in a browser can outlive the entry it names — this
 * table is edited — and a menu that renders nothing is worse than one showing the wrong branch.
 */
export function standardByKey(key: string): Standard {
  const found = standards.find((s) => s.key === key)
  if (found) return found
  const fallback = standards.find((s) => s.key === defaultStandardKey) ?? standards[0]
  if (!fallback) throw new Error('the standards table is empty')
  return fallback
}
